// Created: 2026-08-14
// Updated: 2026-08-14
//
// The single source of key truth for the CRAFT ROOM section (react-query-craft-room, Req 1.2). Nothing
// else in the codebase constructs a craft-room key.
//
// Inherited conventions (slices 1–7): keys are hierarchical arrays of JSON-serializable primitives,
// element 0 names the resource, PERSONAL data is namespaced under a leading 'me' and its query is always
// token-gated, and a parameter earns a place in a key only when it selects a DIFFERENT resource rather
// than filtering one.
//
// ─── THERE ARE TWO KEYS, AND THE TRANSPORT IS WHAT FORCES THE SPLIT ───────────────────────────────
//
// Slice 5 (collections) met the identical surface temptation — an owner view and a visitor view of one
// resource — and correctly chose ONE key. Its evidence: ONE fetcher, ONE URL, and a handler that read no
// identity at all, so the bytes an owner received were the bytes a visitor received. Two keys there would
// have been two copies of one payload, and the second copy would have been the one that went stale.
//
// Here every row of the same evidence points the other way:
//
//   | evidence            | OWNER read                        | VISITOR read                          |
//   |---------------------|-----------------------------------|---------------------------------------|
//   | fetcher             | `fetchSecureCraftRoomPage`        | `fetchCraftRoomPage`                  |
//   | API base            | `SECURE_API_BASE`                 | `API_BASE`                            |
//   | path                | `/craft-room/{screenName}`        | `/craft-rooms/{screenName}`           |
//   | headers             | `Authorization: Bearer`,          | none                                  |
//   |                     | `Content-Type`, `credentials`     |                                       |
//   | payload TYPE        | `SecureCraftRoomPageResponse`     | `CraftRoomPageResponse`               |
//   | screen-name SOURCE  | `useAuth().screen_name`           | `useParams()` via `CraftRoom.tsx`     |
//
// Two fetchers, two bases, two paths, two payload TYPES, and — the decisive row — two screen-name
// SOURCES. That last row is what makes these not one read at two privilege levels but two reads of two
// different things: the owner read CANNOT be pointed at another user's craft room (its argument comes from
// the session), and the visitor read CANNOT be pointed at the session (its argument comes from the route).
//
// THE GENERAL RULE, for later slices to cite: *the number of keys follows the transport and the handler —
// never the component tree, never the route guard, and never whether the UI happens to look the same.*
// ONE fetcher and an identity-blind handler ⇒ ONE key, even when two components read it. TWO fetchers
// answering TWO types ⇒ TWO keys, even when one page renders either. Slice 5 and slice 8 apply the
// identical rule to opposite evidence and reach opposite conclusions; that is the rule working, not two
// conventions (Req 1.3, 1.5).
//
// ─── NEITHER KEY GETS A PER-FIELD SIBLING ─────────────────────────────────────────────────────────
//
// The temptation is `['me','craftRoom','materials']`, `['me','craftRoom','projects']` — one key per
// rendered slice, each invalidated independently. It cannot work: ONE request returns the craft room, the
// materials AND all three project arrays. Splitting the key cannot split the fetch, so a per-field key
// could only hold a SECOND COPY of bytes that already exist under the first key — and the second copy
// would be the one that goes stale (Req 1.8). Materials are written into the one entry by the provider's
// `updateMaterials` (`setQueryData` on `me()`), which is only coherent while there is exactly one place
// the materials live.
//
// No `staleTime` constant either: this section takes the client-wide baseline, and `queries.ts` carries
// the argument for why.

export const craftRoomKeys = {
  /**
   * PERSONAL — the signed-in caller's OWN craft room (the Owner_Entry), holding one
   * `SecureCraftRoomPageResponse` verbatim (Req 1.9). Written by `fetchSecureCraftRoomPage` and by
   * NOTHING ELSE (Req 4.1): there is no public fallback into this entry, because the two payloads name
   * their fields differently and one entry typed `SecureCraftRoomPageResponse` holding a public payload
   * would be a lie. See `page()` below for the escape hatch if an owner-visible PUBLIC read is ever
   * genuinely wanted.
   *
   * ─── WHY NO SCREEN NAME IN THIS KEY (Req 1.4) ───────────────────────────────────────────────────
   *
   * A parameter earns a place in a key only when it selects a DIFFERENT resource. Here it does not: the
   * owner read takes its screen name from `useAuth().screen_name` and from nowhere else, so for a given
   * session there is exactly one value it can ever hold. **The screen name is not a discriminator, it is
   * a restatement of `'me'`.** All three consequences of including it anyway, in order of severity:
   *
   *   1. **One user, two entries.** A screen-name change (the app permits it) opens a second entry while
   *      the first remains in cache holding the same user's inventory under the old name. Two entries for
   *      one resource is exactly the staleness bug the convention exists to prevent.
   *   2. **The sign-out purge stops being able to name its target.** `removeQueries({ queryKey: me() })`
   *      names an exact key. With a screen name in it, the purge would either have to know which name the
   *      ending session used — the value being torn down at exactly that moment — or fall back to prefix
   *      matching, i.e. the broad `['me', …]` purge that belongs to slice 9 (`AuthContext`). A stale entry
   *      from a previous name would survive a purge that targeted the current one.
   *   3. **`refetchCraftRoom()` becomes ambiguous.** It is a bare `() => Promise<void>` at all nine
   *      post-write refresh call sites, none of which pass a screen name and two of which COULD NOT — they
   *      are prop passes (`CraftRoom.tsx:375`, `:377`). A parameterised key would force an argument up
   *      through nine call sites to say the only thing it could ever say.
   *
   * ─── PRERENDERABILITY: NEVER (Req 1.6) ──────────────────────────────────────────────────────────
   *
   * This entry is identity-scoped and token-gated, so it must **never** be dehydrated into shared HTML or
   * seeded during SSG/ISR. What it holds is an inventory plus private and archived project titles; a
   * dehydrated copy in CDN-cached markup would serve one user's craft room to everybody who hit that
   * cache node. The same fact is why the provider both derive-guards on `enabled` and purges this key on
   * sign-out.
   */
  me: () => ['me', 'craftRoom'] as const,

  /**
   * PUBLIC — a craft room being VISITED (the Visitor_Entry), holding one `CraftRoomPageResponse` verbatim.
   * Keyed on the route's `screenName`, which is a different resource per value, so the parameter earns its
   * place here in a way it does not in `me()`.
   *
   * PRERENDERABLE (Req 1.7): identity-invariant and keyed on a stable route parameter, so it may be
   * dehydrated / seeded under the standing SSG/ISR rule. It also survives sign-out untouched — it is
   * public, like every other public entry (Req 10.8).
   *
   * ─── THE ESCAPE HATCH, written here because this is where you will be standing (Req 4.6) ─────────
   *
   * If an owner-visible *public* read of the owner's own craft room is ever genuinely wanted — the thing
   * the deleted `fetchCraftRoom` fallback was reaching for when `idToken` was absent — it uses **this**
   * key with the owner's own screen name: `useCraftRoomPageQuery(screen_name)`. A public payload, under a
   * public key, with the public type. **Never** a public payload written into `me()`: that entry is typed
   * `SecureCraftRoomPageResponse`, the public payload nests its inventory at `craftRoom.materials` and
   * carries neither `private_projects` nor `archived_projects`, and a derivation written against one reads
   * `undefined` from the other. `useState` absorbed that divergence because six separate setters each
   * translated their own branch; ONE cache entry has no setters to translate.
   */
  page: (screenName: string) => ['craftRoom', screenName] as const,
} as const

/** The union of every key this factory emits — used by the key tests. */
export type CraftRoomKey =
  | ReturnType<typeof craftRoomKeys.me>
  | ReturnType<typeof craftRoomKeys.page>
